import type { QuestionnaireItemAnswerOption } from 'fhir/r4';
import { useState, useEffect } from 'react';
import { Checkbox, Radio } from 'semantic-ui-react';
import { QuestionnaireExtensionHelper } from 'utils/Helpers';

import {
  getInitialSelectedAnswers,
  sanitizeQuestionnaireResponseAnswer,
  sanitizeQuestionnaireResponseAnswers,
} from '../fhir-helpers';

import type { QuestionnaireChoiceProps } from './item-props';

export default function Choice(props: QuestionnaireChoiceProps) {
  //props: question, addAnswer, i, j, currentAnswer, enable

  const [selectedAnswer, setSelectedAnswer] = useState<fhir4.QuestionnaireResponseItemAnswer[]>([]);
  const extHelper = new QuestionnaireExtensionHelper(props.question?.extension);

  useEffect(() => {
    // Kept as an expression statement: an `if` would emit different JavaScript.
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    props.currentAnswer
      ? setSelectedAnswer(sanitizeQuestionnaireResponseAnswers(props.currentAnswer))
      : setSelectedAnswer([]);
    const initialSelectedAnswers = getInitialSelectedAnswers(props.question);
    if (
      props.enable &&
      initialSelectedAnswers.length > 0 &&
      (!props.currentAnswer || props.currentAnswer?.length == 0)
    ) {
      setSelectedAnswer(initialSelectedAnswers);
      props.addAnswer(initialSelectedAnswers, props.i, props.j);
    }
  }, [props]);

  const optionIndexInSelectedAnswer = (option: QuestionnaireItemAnswerOption) => {
    for (let i = 0; i < selectedAnswer.length; i++) {
      const selected = selectedAnswer[i]!;
      if (selected.valueCoding && option.valueCoding) {
        if (selected.valueCoding.code === option.valueCoding.code) {
          return i;
        }
      } else if (selected.valueInteger !== undefined && option.valueInteger !== undefined) {
        if (selected.valueInteger === option.valueInteger) {
          return i;
        }
      }
    }
    return -1;
  };

  const handleClick = (option: QuestionnaireItemAnswerOption) => {
    // A FHIR interface has no implicit index signature, so it does not satisfy the
    // helper's `Record<string, unknown>` parameter.
    const responseAnswer = sanitizeQuestionnaireResponseAnswer(option as unknown as Record<string, unknown>);
    if (!responseAnswer) return;

    if (extHelper.isCheckbox()) {
      const index = optionIndexInSelectedAnswer(option);
      const arr = JSON.parse(JSON.stringify(selectedAnswer)) as fhir4.QuestionnaireResponseItemAnswer[];
      // Kept as an expression statement: an `if` would emit different JavaScript.
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      index > -1 ? arr.splice(index, 1) : arr.push(responseAnswer);
      arr.sort((a, b) => {
        // SUSPECTED DEFECT — REPORTED, NOT FIXED. `Coding.code` is a FHIR *string*, so
        // this subtraction is always `NaN` and the comparator never reorders anything:
        // the checkbox answers stay in click order rather than code order. Casting to
        // `number` keeps that exact arithmetic; comparing the strings properly would
        // change the order answers are persisted in. Recorded as KNOWN_BUGS.md Q-22.
        return (a.valueCoding!.code as unknown as number) - (b.valueCoding!.code as unknown as number);
      });
      setSelectedAnswer(arr);
      props.addAnswer(arr, props.i, props.j);
    } else {
      setSelectedAnswer([responseAnswer]);
      props.addAnswer([responseAnswer], props.i, props.j);
    }
  };

  const handleLabel = (option: QuestionnaireItemAnswerOption) => {
    let displayValue: string | number | undefined;
    // Passing an absent language through is what already happens — it matches no
    // translation and the `display` branch below runs. Asserting keeps that path;
    // defaulting to a language code would pick a translation nobody asked for.
    const chosenLang = props.language!;
    if (option?.valueCoding) {
      const extHelper = new QuestionnaireExtensionHelper(option?.valueCoding?.extension);
      if (extHelper.getTargetTranslation(chosenLang)) {
        displayValue = extHelper.getTargetTranslation(chosenLang);
      } else {
        displayValue = option.valueCoding.display;
      }
    } else {
      displayValue = option?.valueInteger;
    }
    return displayValue;
  };

  return (
    <div>
      {props.question.answerOption &&
        props.question.answerOption.map((option, i) => (
          <div key={i}>
            {!extHelper.isEmpty() && extHelper.isCheckbox() && (
              <Checkbox
                disabled={!props.enable}
                label={handleLabel(option)}
                checked={optionIndexInSelectedAnswer(option) > -1}
                onChange={() => handleClick(option)}
              />
            )}
            {((!extHelper.isEmpty() && extHelper.isRadioButton()) ||
              extHelper.getItemControlCode() === undefined ||
              (!extHelper.isEmpty() && extHelper.isSlider())) && (
              //we want to show the slider type for NPS question in web survey, it needs tobe adjusted to display as choice as usual here
              <Radio
                disabled={!props.enable}
                label={handleLabel(option)}
                checked={optionIndexInSelectedAnswer(option) > -1}
                onChange={() => handleClick(option)}
              />
            )}
            <br></br>
          </div>
        ))}
    </div>
  );
}
